import { useCallback, useEffect, useRef } from "react";

/**
 * Iframe für die HTML-Vorschau einer Mahnung (blob:-URL).
 *
 * Blob-Dokumente erben die CSP der App (`script-src 'self'`), Inline-Skripte im
 * Vorschau-HTML werden daher blockiert. Das Einpassen der DIN-A4-Seite auf die
 * Rahmenbreite passiert deshalb hier – aus dem App-Bundle heraus, über den
 * gleichberechtigten Zugriff auf `contentDocument` (blob: ist same-origin).
 */
export function MahnungVorschauFrame({
  src,
  title = "Mahnungs-Vorschau",
  className,
}: {
  src: string;
  title?: string;
  className?: string;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  const fit = useCallback(() => {
    const iframe = frameRef.current;
    let doc: Document | null = null;
    try {
      doc = iframe?.contentDocument ?? null;
    } catch {
      return;
    }
    if (!iframe || !doc) return;
    const shell = doc.querySelector<HTMLElement>(".gd-preview-shell");
    const viewport = doc.getElementById("gd-preview-viewport");
    const page = doc.getElementById("gd-preview-page");
    if (!shell || !viewport || !page) return;

    page.style.transform = "none";
    page.style.width = "210mm";
    page.style.height = "297mm";
    const pageWidth = page.offsetWidth;
    const pageHeight = page.offsetHeight;
    const available = shell.clientWidth || iframe.clientWidth;
    if (!pageWidth || !pageHeight || !available) return;

    const scale = Math.min(1, available / pageWidth);
    page.style.transform = `scale(${scale})`;
    page.style.transformOrigin = "top left";
    viewport.style.width = `${Math.ceil(pageWidth * scale)}px`;
    viewport.style.height = `${Math.ceil(pageHeight * scale)}px`;
    viewport.style.margin = "0 auto";
    viewport.style.overflow = "hidden";
    shell.classList.add("gd-preview-ready");
  }, []);

  useEffect(() => {
    const iframe = frameRef.current;
    if (!iframe) return;

    const onLoad = () => {
      fit();
      // Schriften/Briefpapier können die Maße noch minimal ändern.
      window.setTimeout(fit, 80);
      window.setTimeout(fit, 300);
    };
    iframe.addEventListener("load", onLoad);
    // Falls das Dokument bereits fertig ist, bevor der Listener hing.
    if (iframe.contentDocument?.readyState === "complete") onLoad();

    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => fit()) : null;
    observer?.observe(iframe);

    return () => {
      iframe.removeEventListener("load", onLoad);
      observer?.disconnect();
    };
  }, [fit, src]);

  return <iframe ref={frameRef} title={title} src={src} className={className} />;
}
